"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRight, IconCircleX, IconSearch } from "@tabler/icons-react";
import { formatForDisplay, useHotkey } from "@tanstack/react-hotkeys";
import { useAtom } from "jotai";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { toast } from "sonner";
import { useIsClient } from "@/hooks/use-is-client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouter } from "@/hooks/use-router";
import { analytics } from "@/lib/analytics/client";
import { pendingDomainAtom } from "@/lib/atoms/search-atoms";
import { safeDecodeURIComponent } from "@/lib/safe-parse";
import { Field, FieldLabel } from "@domainstack/ui/field";
import { Form } from "@domainstack/ui/form";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@domainstack/ui/input-group";
import { Kbd } from "@domainstack/ui/kbd";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
import { isValidDomain, normalizeDomainInput } from "@domainstack/utils/domain/client";
const SEARCH_HOTKEY = "Mod+K";
export type SearchClientVariant = "sm" | "lg";
export type SearchClientProps = {
  variant?: SearchClientVariant;
  initialValue?: string;
  onFocusChangeAction?: (isFocused: boolean) => void;
};
function getRoutePrefill(routeDomain: string | undefined): string {
  if (!routeDomain) return "";
  return safeDecodeURIComponent(routeDomain) ?? "";
}
function getDerivedInitialValue(variant: SearchClientVariant, routeDomain: string | undefined, initialValue: string): string {
  const rawInitial = variant === "sm" ? getRoutePrefill(routeDomain) : initialValue;
  const normalizedInitial = normalizeDomainInput(rawInitial);
  return isValidDomain(normalizedInitial) ? normalizedInitial : "";
}
function getSearchPlaceholder(variant: SearchClientVariant, mounted: boolean, isMobile: boolean): string {
  if (variant === "lg") return "domainstack.io\u2026";
  if (mounted && isMobile) return "Search\u2026";
  return "Search any domain\u2026";
}
function SearchInputAddons(t0) {
  const $ = _c(9);
  const {
    variant,
    loading,
    mounted,
    isFocused
  } = t0;
  if (variant === "sm" && (loading || mounted)) {
    let t1;
    if ($[0] !== isFocused || $[1] !== loading) {
      t1 = <InputGroupAddon align="inline-end">{loading ? <Spinner /> : <Kbd className="hidden border bg-muted/80 px-1.5 py-0.5 sm:inline-flex">{isFocused ? "Esc" : formatForDisplay(SEARCH_HOTKEY, {
            separatorToken: "\xA0"
          })}</Kbd>}</InputGroupAddon>;
      $[0] = isFocused;
      $[1] = loading;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (variant === "lg") {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-[13px]">Inspect</span>;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== loading) {
      t2 = loading ? <Spinner /> : <><Kbd className="hidden text-[13px] sm:inline-flex">⏎</Kbd><IconArrowRight className="inline-flex sm:hidden" aria-hidden={true} /></>;
      $[4] = loading;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== loading || $[7] !== t2) {
      t3 = <InputGroupAddon align="inline-end"><InputGroupButton type="submit" disabled={loading} className="mx-1 h-8 disabled:pointer-events-none" variant="ghost">{t1}{t2}</InputGroupButton></InputGroupAddon>;
      $[6] = loading;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  return null;
}
function useSearchClient(t0) {
  const $ = _c(38);
  const {
    variant,
    initialValue,
    onFocusChangeAction
  } = t0;
  const router = useRouter();
  const params = useParams();
  const isMobile = useIsMobile();
  const [pendingDomain, setPendingDomain] = useAtom(pendingDomainAtom);
  let t1;
  if ($[0] !== initialValue || $[1] !== params.domain || $[2] !== variant) {
    t1 = getDerivedInitialValue(variant, params.domain, initialValue);
    $[0] = initialValue;
    $[1] = params.domain;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const derivedInitial = t1;
  const [value, setValue] = useState(derivedInitial);
  const [prevDerivedInitial, setPrevDerivedInitial] = useState(derivedInitial);
  const [loading, startNavigation] = useTransition();
  const mounted = useIsClient();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  if (derivedInitial !== prevDerivedInitial) {
    setPrevDerivedInitial(derivedInitial);
    setValue(derivedInitial);
  }
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      inputRef.current?.focus();
    };
    t3 = {
      conflictBehavior: "allow"
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useHotkey(SEARCH_HOTKEY, t2, t3);
  let t4;
  if ($[6] !== router) {
    t4 = domain => {
      const target = normalizeDomainInput(domain);
      analytics.track("search_submitted", {
        domain: target
      });
      startNavigation(() => router.push(`/${encodeURIComponent(target)}`));
    };
    $[6] = router;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const navigateToDomain = t4;
  const navigateRef = useRef(navigateToDomain);
  let t5;
  if ($[8] !== navigateToDomain) {
    t5 = () => {
      navigateRef.current = navigateToDomain;
    };
    $[8] = navigateToDomain;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t5);
  if (variant === "lg" && pendingDomain && value !== pendingDomain) {
    setValue(pendingDomain);
  }
  let t6;
  let t7;
  if ($[10] !== pendingDomain || $[11] !== setPendingDomain || $[12] !== variant) {
    t6 = () => {
      if (variant === "lg" && pendingDomain) {
        navigateRef.current(pendingDomain);
        setPendingDomain(null);
      }
    };
    t7 = [variant, pendingDomain, setPendingDomain];
    $[10] = pendingDomain;
    $[11] = setPendingDomain;
    $[12] = variant;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  const pointerDownRef = useRef(false);
  const justFocusedRef = useRef(false);
  let t8;
  if ($[15] !== variant) {
    t8 = () => {
      if (variant !== "sm") {
        return;
      }
      pointerDownRef.current = true;
    };
    $[15] = variant;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handlePointerDown = t8;
  let t9;
  if ($[17] !== onFocusChangeAction) {
    t9 = e => {
      setIsFocused(true);
      onFocusChangeAction?.(true);
      if (!pointerDownRef.current) {
        e.currentTarget.select();
        justFocusedRef.current = false;
      } else {
        justFocusedRef.current = true;
        pointerDownRef.current = false;
      }
    };
    $[17] = onFocusChangeAction;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleFocus = t9;
  let t10;
  if ($[19] !== onFocusChangeAction) {
    t10 = () => {
      setIsFocused(false);
      onFocusChangeAction?.(false);
    };
    $[19] = onFocusChangeAction;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleBlur = t10;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_0 => {
      if (e_0.detail === 3) {
        e_0.currentTarget.select();
        justFocusedRef.current = false;
        return;
      }
      if (justFocusedRef.current && e_0.detail === 1) {
        e_0.currentTarget.select();
      }
      justFocusedRef.current = false;
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleClick = t11;
  let t12;
  if ($[22] !== onFocusChangeAction) {
    t12 = e_1 => {
      if (e_1.key === "Escape") {
        e_1.preventDefault();
        e_1.stopPropagation();
        e_1.currentTarget.blur();
        setIsFocused(false);
        onFocusChangeAction?.(false);
      }
    };
    $[22] = onFocusChangeAction;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleKeyDown = t12;
  let t13;
  if ($[24] !== value) {
    t13 = () => {
      setIsFocused(false);
      inputRef.current?.blur();
      const normalized = normalizeDomainInput(value);
      if (!isValidDomain(normalized)) {
        analytics.track("search_invalid_input", {
          input: value
        });
        toast.error("Please enter a valid domain.", {
          icon: <IconCircleX className="size-4" />,
          position: "bottom-center"
        });
        inputRef.current?.focus();
        return;
      }
      navigateRef.current(normalized);
    };
    $[24] = value;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleSubmit = t13;
  let t14;
  if ($[26] !== handleBlur || $[27] !== handleFocus || $[28] !== handleKeyDown || $[29] !== handlePointerDown || $[30] !== handleSubmit || $[31] !== isFocused || $[32] !== isMobile || $[33] !== loading || $[34] !== mounted || $[35] !== value || $[36] !== variant) {
    t14 = {
      variant,
      value,
      setValue,
      loading,
      mounted,
      isMobile,
      isFocused,
      inputRef,
      handlePointerDown,
      handleFocus,
      handleBlur,
      handleClick,
      handleKeyDown,
      handleSubmit
    };
    $[26] = handleBlur;
    $[27] = handleFocus;
    $[28] = handleKeyDown;
    $[29] = handlePointerDown;
    $[30] = handleSubmit;
    $[31] = isFocused;
    $[32] = isMobile;
    $[33] = loading;
    $[34] = mounted;
    $[35] = value;
    $[36] = variant;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
export function SearchClient(t0) {
  const $ = _c(40);
  const {
    variant: t1,
    initialValue: t2,
    onFocusChangeAction
  } = t0;
  const variant = t1 === undefined ? "lg" : t1;
  const initialValue = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== initialValue || $[1] !== onFocusChangeAction || $[2] !== variant) {
    t3 = {
      variant,
      initialValue,
      onFocusChangeAction
    };
    $[0] = initialValue;
    $[1] = onFocusChangeAction;
    $[2] = variant;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    value,
    setValue,
    loading,
    mounted,
    isMobile,
    isFocused,
    inputRef,
    handlePointerDown,
    handleFocus,
    handleBlur,
    handleClick,
    handleKeyDown,
    handleSubmit
  } = useSearchClient(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "tool-name": "domain-search",
      "tool-description": "Look up WHOIS, DNS, SSL, hosting, HTTP headers, and SEO for any domain"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FieldLabel className="sr-only">Domain</FieldLabel>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = variant === "lg" ? "h-12" : "h-10";
  let t7;
  if ($[6] !== t6) {
    t7 = cn(t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      "tool-param-description": "Domain name to look up, e.g. example.com"
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = variant === "lg" && mounted && !isMobile;
  let t10;
  if ($[9] !== isMobile || $[10] !== mounted || $[11] !== variant) {
    t10 = getSearchPlaceholder(variant, mounted, isMobile);
    $[9] = isMobile;
    $[10] = mounted;
    $[11] = variant;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== setValue) {
    t11 = e => setValue(e.target.value);
    $[13] = setValue;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== handleBlur || $[16] !== handleClick || $[17] !== handleFocus || $[18] !== handleKeyDown || $[19] !== handlePointerDown || $[20] !== inputRef || $[21] !== loading || $[22] !== t10 || $[23] !== t11 || $[24] !== t9 || $[25] !== value) {
    t12 = <InputGroupInput ref={inputRef} name="q" {...t8} autoFocus={t9} inputMode="url" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} disabled={loading} placeholder={t10} aria-label="Search any domain" value={value} onChange={t11} onPointerDown={handlePointerDown} onFocus={handleFocus} onBlur={handleBlur} onClick={handleClick} onKeyDown={handleKeyDown} className="relative truncate sm:translate-y-[1px]" />;
    $[15] = handleBlur;
    $[16] = handleClick;
    $[17] = handleFocus;
    $[18] = handleKeyDown;
    $[19] = handlePointerDown;
    $[20] = inputRef;
    $[21] = loading;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = value;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <InputGroupAddon><IconSearch aria-hidden={true} /></InputGroupAddon>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isFocused || $[29] !== loading || $[30] !== mounted || $[31] !== variant) {
    t14 = <SearchInputAddons variant={variant} loading={loading} mounted={mounted} isFocused={isFocused} />;
    $[28] = isFocused;
    $[29] = loading;
    $[30] = mounted;
    $[31] = variant;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t12 || $[34] !== t14 || $[35] !== t7) {
    t15 = <Field>{t5}<div className="relative w-full flex-1"><InputGroup className={t7}>{t12}{t13}{t14}</InputGroup></div></Field>;
    $[33] = t12;
    $[34] = t14;
    $[35] = t7;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== handleSubmit || $[38] !== t15) {
    t16 = <div className="flex w-full flex-col gap-5"><Form aria-label="Domain search" action="/" method="GET" {...t4} onFormSubmit={handleSubmit}>{t15}</Form></div>;
    $[37] = handleSubmit;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}